/**
 * What a member sees when an upload is refused (#758).
 *
 * ── Two refusals, and they must not say the same thing ──────────────────
 *
 * `MEMBER_CHAT_STORAGE_EXCEEDED` is the member's own ceiling: they can fix
 * it themselves, by clearing conversations or moving files to the Drive.
 * `STORAGE_QUOTA_EXCEEDED` is the organisation's: clearing their own files
 * frees nothing, and the dialog says so outright — a member who is told
 * "not enough space" by a full ORGANISATION will otherwise spend an
 * afternoon deleting their own work and watch the number not move.
 *
 * ── Composed from the figures, not from the server's sentence ───────────
 *
 * The refusal carries a ready-made English message. Showing it would be
 * easier and would be untranslatable, so the dialog reads `usedBytes` /
 * `limitBytes` off the body and writes the sentence in the member's
 * language. The server's string stays for API consumers and logs.
 *
 * ── Why "ask an admin" is not offered HERE ──────────────────────────────
 *
 * It exists now. `chat_storage_more_space`, `POST
 * /:id/chat-storage/request-more` and the admin's side of it all ship on
 * this branch, and `MyFigure` on the storage screen calls it. An earlier
 * version of this note said the feature did not exist and was "a feature of
 * its own"; that stopped being true in the same PR (full review 2,
 * finding 8).
 *
 * Its absence from THIS dialog is now a deliberate gap, not a missing
 * prerequisite. The dialog appears mid-chat, over whatever the member was
 * doing, and an ask sent from here cannot be reviewed or taken back before
 * it reaches every admin. The button is one click away, on the storage
 * screen that "Manage chat storage" already opens.
 *
 * Worth revisiting — a member refused mid-send is exactly who wants it —
 * but that is a product call, filed in dw/issues-to-file.md for Season.
 */
import { useTranslation } from '@papan/i18n'
import { formatBytes } from '../budget/bits'

export type StorageBlockedKind = 'member' | 'organisation'

export function StorageBlockedDialog({
  kind, usedBytes, limitBytes, onClose, onManage,
}: {
  kind: StorageBlockedKind
  usedBytes?: number
  limitBytes?: number
  onClose: () => void
  /** Settings → Storage. Absent on surfaces that cannot navigate there. */
  onManage?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const mine = kind === 'member'
  const haveFigures = typeof usedBytes === 'number' && typeof limitBytes === 'number'
  // A limit of ZERO is its own state. "You have used 0 B of your 0 B"
  // followed by "clear out conversations you no longer need" sent the
  // member off deleting things for an afternoon while the paperclip kept
  // refusing — nothing to clear, and clearing cannot get anyone under
  // zero. `blockedLimitBody` already exists in all four locales for
  // exactly this; this dialog, the one they actually meet at the moment
  // of refusal, was the only surface that never took the branch.
  const blockedAtZero = limitBytes === 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className="relative z-10 max-w-md w-full flex flex-col ws-glass-modal ws-anim-modal-in">
        <div className="flex gap-3.5 px-5 pt-5">
          <span className={`w-9 h-9 rounded-xl shrink-0 flex items-center justify-center ${mine ? 'bg-amber-500/15' : 'bg-rose-500/15'}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={mine ? '#f59e0b' : '#f43f5e'} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {mine
                ? <><path d="M12 3l9.5 16.5h-19z" /><path d="M12 10v4M12 17h.01" /></>
                : <><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>}
            </svg>
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text">
              {t(mine ? 'settings.storage.storageBlockedMineTitle' : 'settings.storage.storageBlockedOrgTitle')}
            </h3>
            <p className="text-sm themed-text-secondary mt-1.5 leading-relaxed">
              {/* The figures are omitted rather than guessed when the
                  refusal did not carry them — a cross-tenant write
                  deliberately withholds them, and inventing a number here
                  would be worse than one sentence fewer. */}
              {mine && blockedAtZero
                ? t('settings.storage.blockedLimitBody')
                : mine && haveFigures
                  ? t('settings.storage.storageBlockedMineBody', { used: formatBytes(usedBytes!), limit: formatBytes(limitBytes!) })
                  : t(mine ? 'settings.storage.storageBlockedMineBody' : 'settings.storage.storageBlockedOrgBody', { used: '—', limit: '—' })}
            </p>
            {/* The "how" line is advice, and at a limit of ZERO every
                word of it is wrong: there is nothing to clear, and moving
                files to the Drive cannot get anyone under zero. The
                sentence above already says the one true thing — ask an
                admin — so this is dropped rather than contradicted (full
                review 4C, finding 3). */}
            {/* ── ONE ACTION, UNCONDITIONALLY TRUE, AND FINDABLE ───────
                This sentence took FOUR wrong versions. Each tried to tell
                the member something about moving a file to Drive, and each
                was wrong in a different direction:

                  1. "moving them frees this quota" — wrong AT the limit,
                     where an inline move is refused.
                  2. "will not help while you are at the limit" — wrong
                     BELOW it. The dialog opens for ANY refusal, so a
                     member with 20 MB left who attached 30 MB was talked
                     out of a move that works.
                  3. "only a file smaller than the space you have left" —
                     wrong for a handle-backed file. The DIALOG's move
                     re-uploads the bytes only when the chip has no CAS
                     handle, and that upload is gated; with a handle it
                     goes straight to the save endpoint, which is not. (Two
                     earlier versions of this comment each put a CENSUS of
                     the member gate's call sites here and each got it
                     wrong — three paths, then five, actually six, and the
                     second attempt's closing line said `save-to-drive` is
                     not among them when the agent's own `save_to_drive`
                     TOOL does reach it, inert only because it passes no
                     `uploadedByEmail`. No census here any more — but the
                     replacement needs BOTH hops, and the first version of
                     it said something false about the first: grep
                     `assertMemberChatStorageWithin` (two call sites), then
                     grep the callers of the two functions enclosing them,
                     and check each passes an uploader email. Six paths
                     today, exactly one inert. Only the `tree-storage` site
                     guards at the call; the metering one passes a
                     possibly-undefined email straight through and the
                     guard is `if (!opts.email) return` inside the gate, so
                     "both of which guard on that field" was wrong.)
                  4. "start with your largest chat files" — wrong for a
                     member who HAS no handle-backed file. `canInline` is
                     an explicit list — png, jpeg, gif, webp, svg, pdf and
                     a few text/json/xml types — plus anything `text/*`,
                     all under 10 MB. A member whose chat history is pasted
                     screenshots and small PDFs therefore has zero
                     handle-backed files, so every file she tries is the
                     refused path — and `describeSaveError` has no case for
                     that code, so she gets the raw server sentence. (Not
                     "every `image/*`", which an earlier version of this
                     comment claimed: heic, avif, bmp and tiff are NOT on
                     the list, so an AirDropped iPhone photo is
                     handle-backed and moves fine.) Worse, no screen in the
                     product shows a member their chat files or their
                     sizes, so "your largest" named a ranking that exists
                     only in the database.

                So: no claim about Drive at all, which is also what the
                `chat_storage_declined` bell body settled on. Deleting a
                conversation releases both legs — `unmeterInlineSession`
                for the inline ledger and `deleteBySession` for the CAS
                rows, both in the delete route.
                ONE CAVEAT, and it is the dedup rule rather than a bug: the
                inline leg is a plain SUM, so a delete always moves the
                figure, but the CAS leg is DISTINCT ON (uploader, hash), so
                deleting one conversation frees nothing if the member holds
                the same hash elsewhere — the same file attached twice, or
                a fork of their own conversation, whose clone row carries
                the same hash. Deleting every copy does free it, so the
                sentence holds for "conversations you no longer need"
                (plural) and an earlier version of this comment claiming it
                is true "for every member regardless of what their files
                are" was absolutism (scoped review of 6db2ef03b, B3).
                Where the member can act on it is filed separately — see
                dw/issues-to-file.md; the page this dialog's button leads to
                lists her conversations read-only.

                The inline-move dead end is filed (dw/issues-to-file.md)
                and pinned by a test. It does not belong in a sentence:
                four attempts, four wrong directions. */}
            {/* `mine &&`, matching the body's own guard. Bare
                `!blockedAtZero` dropped the advice on the ORGANISATION
                branch too, where `limitBytes` is the tenant quota — so a
                tenant whose quota resolves to 0 lost "clearing your own
                files will not help here, an admin needs to add storage",
                which is the one sentence this dialog exists to say
                (scoped review of the convergence pass, finding 6). */}
            {!(mine && blockedAtZero) && (
              <p className="text-sm themed-text-secondary mt-2 leading-relaxed">
                {t(mine ? 'settings.storage.storageBlockedMineHow' : 'settings.storage.storageBlockedOrgHow')}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 mt-1">
          {mine && onManage && (
            <button
              type="button"
              onClick={() => { onManage(); onClose() }}
              className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white"
            >
              {t('settings.storage.storageBlockedManage')}
            </button>
          )}
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg themed-border border themed-text">
            {t('settings.storage.storageBlockedGotIt')}
          </button>
        </div>
      </div>
    </div>
  )
}
